import { httpsCallable } from "firebase/functions";
import { auth, functions } from "@/firebase/authorityClient";
import {
  isIdentityAuthorityCommandsFeatureEnabled,
  isIdentityAuthorityReadsFeatureEnabled,
} from "@/config/runtimeConfig";
import { withUiDeadline } from "@/utils/uiDeadline";

const SCHEMA_VERSION = 1;
const OPERATION_ID_PATTERN = /^[A-Za-z0-9_-]{16,128}$/;
const SHA256_PATTERN = /^[a-f0-9]{64}$/;
const PUBLIC_IDENTIFIER_PATTERN = /^[a-z0-9][a-z0-9_-]{2,63}$/;
const ASSET_ID_PATTERN = /^[a-f0-9]{64}$/;
const EVM_ADDRESS_PATTERN = /^0x[a-f0-9]{40}$/;
const CONTROL_CHARACTERS =
  /[\u0000-\u001f\u007f-\u009f\u061c\u200e\u200f\u202a-\u202e\u2066-\u2069]/;

const PROFILE_FIELD_KEYS = Object.freeze([
  "bio",
  "site",
  "organisations",
  "posts",
  "products",
  "services",
  "externalWallets",
]);
const PROFILE_AUDIENCES = new Set(["private", "authenticated", "public"]);
const HOLDER_AUDIENCES = new Set(["public", "members", "administrators"]);
const HOLDER_DISCLOSURE_POLICIES = new Set([
  "transparent",
  "member-choice",
  "confidential",
]);
const EXTERNAL_WALLET_AUDIENCES = new Set(["private", "public"]);
const EXTERNAL_WALLET_CHAINS = new Set([
  "eip155:1",
  "eip155:10",
  "eip155:137",
  "eip155:8453",
  "eip155:42161",
  "eip155:42220",
]);
const PROFILE_KEYS = Object.freeze([
  "displayName",
  "avatarAssetId",
  "bio",
  "site",
  "organisations",
  "posts",
  "products",
  "services",
  "externalWallets",
]);
const ACCOUNT_KEYS = Object.freeze([
  "email",
  "displayName",
  "avatarAssetId",
  "bio",
  "site",
]);

export class IdentityAuthorityServiceError extends Error {
  constructor(code = "unknown", message = "Identity settings are unavailable.") {
    super(message);
    this.name = "IdentityAuthorityServiceError";
    this.code = code;
  }
}

function fail(code, message = "Identity settings are unavailable.") {
  throw new IdentityAuthorityServiceError(code, message);
}

function isPlainRecord(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
}

function hasExactKeys(value, expectedKeys) {
  if (!isPlainRecord(value)) return false;
  const ownKeys = Reflect.ownKeys(value);
  const descriptors = Object.getOwnPropertyDescriptors(value);
  const actualKeys = ownKeys
    .filter((key) => typeof key === "string")
    .sort();
  const sortedExpectedKeys = [...expectedKeys].sort();
  return (
    ownKeys.every(
      (key) =>
        typeof key === "string" &&
        descriptors[key]?.enumerable === true &&
        Object.prototype.hasOwnProperty.call(descriptors[key], "value"),
    ) &&
    actualKeys.length === sortedExpectedKeys.length &&
    actualKeys.every((key, index) => key === sortedExpectedKeys[index])
  );
}

function requireExactKeys(value, keys, code = "invalid-argument") {
  if (!hasExactKeys(value, keys)) fail(code);
  return value;
}

function normalizeOperationId(value) {
  if (typeof value !== "string" || !OPERATION_ID_PATTERN.test(value)) {
    fail("invalid-argument", "The identity operation is invalid.");
  }
  return value;
}

function normalizeRevision(value, { minimum = 0, code = "invalid-argument" } = {}) {
  if (!Number.isSafeInteger(value) || value < minimum) fail(code);
  return value;
}

function normalizeText(
  value,
  { minimum = 0, maximum, nullable = false, trim = true, code = "invalid-argument" },
) {
  if (nullable && value === null) return null;
  if (typeof value !== "string") fail(code);
  const normalized = trim ? value.trim() : value;
  if (
    normalized.length < minimum ||
    normalized.length > maximum ||
    CONTROL_CHARACTERS.test(normalized) ||
    (!trim && normalized !== normalized.trim())
  ) {
    fail(code);
  }
  return normalized;
}

function normalizeNullableText(value, maximum, code = "invalid-argument") {
  return normalizeText(value, {
    maximum,
    nullable: true,
    trim: false,
    code,
  });
}

function normalizePublicIdentifier(value, code = "invalid-argument") {
  if (typeof value !== "string" || !PUBLIC_IDENTIFIER_PATTERN.test(value)) {
    fail(code);
  }
  return value;
}

function normalizeSha256(value, code = "invalid-argument") {
  if (typeof value !== "string" || !SHA256_PATTERN.test(value)) fail(code);
  return value;
}

function normalizeNullableAssetId(value, code = "invalid-argument") {
  if (value === null) return null;
  if (typeof value !== "string" || !ASSET_ID_PATTERN.test(value)) fail(code);
  return value;
}

function normalizeSite(value, code = "invalid-argument") {
  if (value === null) return null;
  const site = normalizeText(value, {
    minimum: 1,
    maximum: 2048,
    trim: false,
    code,
  });
  try {
    const parsed = new URL(site);
    if (
      parsed.protocol !== "https:" ||
      parsed.username ||
      parsed.password ||
      !parsed.hostname ||
      parsed.toString() !== site
    ) {
      fail(code);
    }
  } catch (error) {
    if (error instanceof IdentityAuthorityServiceError) throw error;
    fail(code);
  }
  return site;
}

function normalizeSiteInput(value) {
  if (value === null || value.trim() === "") return null;
  const candidate = value.trim();
  try {
    const parsed = new URL(candidate);
    if (
      parsed.protocol !== "https:" ||
      parsed.username ||
      parsed.password ||
      !parsed.hostname
    ) {
      fail("invalid-argument");
    }
    return normalizeSite(parsed.toString());
  } catch (error) {
    if (error instanceof IdentityAuthorityServiceError) throw error;
    fail("invalid-argument");
  }
}

function normalizeEmail(value, code = "invalid-response") {
  if (value === null) return null;
  if (
    typeof value !== "string" ||
    value !== value.trim() ||
    value.length < 3 ||
    value.length > 320 ||
    CONTROL_CHARACTERS.test(value) ||
    !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
  ) {
    fail(code);
  }
  return value;
}

function normalizeProfileFields(value, code = "invalid-argument") {
  requireExactKeys(value, PROFILE_FIELD_KEYS, code);
  const result = {};
  for (const key of PROFILE_FIELD_KEYS) {
    if (typeof value[key] !== "boolean") fail(code);
    result[key] = value[key];
  }
  return Object.freeze(result);
}

function normalizeAccountInput(value) {
  requireExactKeys(value, ["displayName", "avatarAssetId", "bio", "site"]);
  const rawBio = value.bio;
  const rawSite = value.site;
  if (
    (rawBio !== null && typeof rawBio !== "string") ||
    (rawSite !== null && typeof rawSite !== "string")
  ) {
    fail("invalid-argument");
  }
  return Object.freeze({
    displayName: normalizeText(value.displayName, {
      minimum: 1,
      maximum: 80,
    }),
    avatarAssetId: normalizeNullableAssetId(value.avatarAssetId),
    bio:
      rawBio === null || rawBio.trim() === ""
        ? null
        : normalizeText(rawBio, { minimum: 1, maximum: 500 }),
    site:
      rawSite === null ? null : normalizeSiteInput(rawSite),
  });
}

function normalizeOutcome(value, allowed, code = "invalid-response") {
  if (!allowed.has(value)) fail(code);
  return value;
}

function normalizeOwnAccount(value) {
  const code = "invalid-response";
  requireExactKeys(
    value,
    ["schemaVersion", "revision", "publicIdentifier", "account", "profilePrivacy"],
    code,
  );
  requireExactKeys(value.account, ACCOUNT_KEYS, code);
  requireExactKeys(value.profilePrivacy, ["revision", "audience", "fields"], code);
  if (
    value.schemaVersion !== SCHEMA_VERSION ||
    !PROFILE_AUDIENCES.has(value.profilePrivacy.audience)
  ) {
    fail(code);
  }
  return Object.freeze({
    schemaVersion: SCHEMA_VERSION,
    revision: normalizeRevision(value.revision, { minimum: 1, code }),
    publicIdentifier: normalizePublicIdentifier(value.publicIdentifier, code),
    account: Object.freeze({
      email: normalizeEmail(value.account.email, code),
      displayName: normalizeText(value.account.displayName, {
        minimum: 1,
        maximum: 80,
        trim: false,
        code,
      }),
      avatarAssetId: normalizeNullableAssetId(value.account.avatarAssetId, code),
      bio: normalizeNullableText(value.account.bio, 500, code),
      site: normalizeSite(value.account.site, code),
    }),
    profilePrivacy: Object.freeze({
      revision: normalizeRevision(value.profilePrivacy.revision, { code }),
      audience: value.profilePrivacy.audience,
      fields: normalizeProfileFields(value.profilePrivacy.fields, code),
    }),
  });
}

function normalizeContentPresentation(value) {
  const code = "invalid-response";
  requireExactKeys(value, ["publicIdentifier", "title", "excerpt"], code);
  return Object.freeze({
    publicIdentifier: normalizePublicIdentifier(value.publicIdentifier, code),
    title: normalizeText(value.title, {
      minimum: 1,
      maximum: 160,
      trim: false,
      code,
    }),
    excerpt: normalizeNullableText(value.excerpt, 1000, code),
  });
}

function normalizeRolePresentation(value) {
  const code = "invalid-response";
  requireExactKeys(value, ["publicIdentifier", "title"], code);
  return Object.freeze({
    publicIdentifier: normalizePublicIdentifier(value.publicIdentifier, code),
    title: normalizeText(value.title, {
      minimum: 1,
      maximum: 100,
      trim: false,
      code,
    }),
  });
}

function normalizeOrganisationPresentation(value) {
  const code = "invalid-response";
  requireExactKeys(
    value,
    ["publicIdentifier", "name", "avatarAssetId", "roles"],
    code,
  );
  if (!Array.isArray(value.roles) || value.roles.length > 12) fail(code);
  const roles = value.roles.map(normalizeRolePresentation);
  if (new Set(roles.map((role) => role.publicIdentifier)).size !== roles.length) {
    fail(code);
  }
  return Object.freeze({
    publicIdentifier: normalizePublicIdentifier(value.publicIdentifier, code),
    name: normalizeText(value.name, {
      minimum: 1,
      maximum: 120,
      trim: false,
      code,
    }),
    avatarAssetId: normalizeNullableAssetId(value.avatarAssetId, code),
    roles: Object.freeze(roles),
  });
}

function normalizeExternalWalletPresentation(value) {
  const code = "invalid-response";
  requireExactKeys(value, ["publicIdentifier", "chain", "address", "label"], code);
  if (
    !EXTERNAL_WALLET_CHAINS.has(value.chain) ||
    typeof value.address !== "string" ||
    !EVM_ADDRESS_PATTERN.test(value.address)
  ) {
    fail(code);
  }
  return Object.freeze({
    publicIdentifier: normalizePublicIdentifier(value.publicIdentifier, code),
    chain: value.chain,
    address: value.address,
    label: normalizeNullableText(value.label, 80, code),
  });
}

function normalizeUniquePresentations(values, normalizer, keySelector) {
  const code = "invalid-response";
  if (!Array.isArray(values) || values.length > 12) fail(code);
  const result = values.map(normalizer);
  const keys = result.map(keySelector);
  if (new Set(keys).size !== keys.length) fail(code);
  return Object.freeze(result);
}

function normalizeProfilePresentation(value, requestedIdentifier) {
  const code = "invalid-response";
  requireExactKeys(value, ["schemaVersion", "publicIdentifier", "profile"], code);
  requireExactKeys(value.profile, PROFILE_KEYS, code);
  if (
    value.schemaVersion !== SCHEMA_VERSION ||
    value.publicIdentifier !== requestedIdentifier
  ) {
    fail(code);
  }
  return Object.freeze({
    schemaVersion: SCHEMA_VERSION,
    publicIdentifier: requestedIdentifier,
    profile: Object.freeze({
      displayName: normalizeText(value.profile.displayName, {
        minimum: 1,
        maximum: 80,
        trim: false,
        code,
      }),
      avatarAssetId: normalizeNullableAssetId(value.profile.avatarAssetId, code),
      bio: normalizeNullableText(value.profile.bio, 500, code),
      site: normalizeSite(value.profile.site, code),
      organisations: normalizeUniquePresentations(
        value.profile.organisations,
        normalizeOrganisationPresentation,
        ({ publicIdentifier }) => publicIdentifier,
      ),
      posts: normalizeUniquePresentations(
        value.profile.posts,
        normalizeContentPresentation,
        ({ publicIdentifier }) => publicIdentifier,
      ),
      products: normalizeUniquePresentations(
        value.profile.products,
        normalizeContentPresentation,
        ({ publicIdentifier }) => publicIdentifier,
      ),
      services: normalizeUniquePresentations(
        value.profile.services,
        normalizeContentPresentation,
        ({ publicIdentifier }) => publicIdentifier,
      ),
      externalWallets: normalizeUniquePresentations(
        value.profile.externalWallets,
        normalizeExternalWalletPresentation,
        ({ chain, address }) => `${chain}:${address}`,
      ),
    }),
  });
}

function normalizeSimpleRevisionResult(value) {
  const code = "invalid-response";
  requireExactKeys(value, ["schemaVersion", "outcome", "revision"], code);
  if (value.schemaVersion !== SCHEMA_VERSION) fail(code);
  return Object.freeze({
    schemaVersion: SCHEMA_VERSION,
    outcome: normalizeOutcome(value.outcome, new Set(["updated", "unchanged"]), code),
    revision: normalizeRevision(value.revision, { minimum: 1, code }),
  });
}

function normalizePrivacyResult(value, request) {
  const code = "invalid-response";
  requireExactKeys(
    value,
    ["schemaVersion", "outcome", "revision", "audience", "fields"],
    code,
  );
  if (
    value.schemaVersion !== SCHEMA_VERSION ||
    value.audience !== request.audience
  ) {
    fail(code);
  }
  const fields = normalizeProfileFields(value.fields, code);
  if (PROFILE_FIELD_KEYS.some((key) => fields[key] !== request.fields[key])) {
    fail(code);
  }
  return Object.freeze({
    schemaVersion: SCHEMA_VERSION,
    outcome: normalizeOutcome(value.outcome, new Set(["updated", "unchanged"]), code),
    revision: normalizeRevision(value.revision, { code }),
    audience: value.audience,
    fields,
  });
}

function expectedEffectiveDisclosureAudience(request, policy) {
  if (policy === "confidential") return "administrators";
  // Organisation visibility may narrow a requested public audience to
  // members. The response must never broaden the actor's request.
  if (request.audience === "public") return new Set(["public", "members"]);
  return new Set([request.audience]);
}

function normalizeDisclosureResult(value, request) {
  const code = "invalid-response";
  requireExactKeys(
    value,
    [
      "schemaVersion",
      "outcome",
      "revision",
      "holderDisclosurePolicy",
      "effectiveAudience",
      "requiredDisclosureEpoch",
    ],
    code,
  );
  if (
    value.schemaVersion !== SCHEMA_VERSION ||
    !HOLDER_DISCLOSURE_POLICIES.has(value.holderDisclosurePolicy) ||
    !HOLDER_AUDIENCES.has(value.effectiveAudience) ||
    !Number.isSafeInteger(value.requiredDisclosureEpoch) ||
    value.requiredDisclosureEpoch < 0
  ) {
    fail(code);
  }
  const allowedAudience = expectedEffectiveDisclosureAudience(
    request,
    value.holderDisclosurePolicy,
  );
  if (
    !allowedAudience.has(value.effectiveAudience) ||
    (value.holderDisclosurePolicy === "transparent" &&
      (request.audience !== "public" ||
        request.acceptedDisclosureEpoch !== value.requiredDisclosureEpoch)) ||
    (value.holderDisclosurePolicy === "confidential" &&
      (request.audience !== "administrators" ||
        request.acceptedDisclosureEpoch !== null)) ||
    (value.holderDisclosurePolicy === "member-choice" &&
      request.acceptedDisclosureEpoch !== null)
  ) {
    fail(code);
  }
  return Object.freeze({
    schemaVersion: SCHEMA_VERSION,
    outcome: normalizeOutcome(value.outcome, new Set(["updated", "unchanged"]), code),
    revision: normalizeRevision(value.revision, { minimum: 1, code }),
    holderDisclosurePolicy: value.holderDisclosurePolicy,
    effectiveAudience: value.effectiveAudience,
    requiredDisclosureEpoch: value.requiredDisclosureEpoch,
  });
}

function normalizeWalletConsentResult(value, request) {
  const code = "invalid-response";
  requireExactKeys(value, ["schemaVersion", "outcome", "revision", "audience"], code);
  if (
    value.schemaVersion !== SCHEMA_VERSION ||
    value.audience !== request.audience
  ) {
    fail(code);
  }
  return Object.freeze({
    schemaVersion: SCHEMA_VERSION,
    outcome: normalizeOutcome(value.outcome, new Set(["updated", "unchanged"]), code),
    revision: normalizeRevision(value.revision, { minimum: 1, code }),
    audience: value.audience,
  });
}

function normalizeServiceError(error) {
  if (error instanceof IdentityAuthorityServiceError) return error;
  const messages = {
    aborted: "This identity record changed elsewhere. Load the latest version and try again.",
    "deadline-exceeded": "Identity settings did not respond in time.",
    "failed-precondition": "Identity settings are temporarily unavailable.",
    "invalid-argument": "The identity request was invalid.",
    "not-found": "This identity is unavailable.",
    "permission-denied": "You do not have access to this identity setting.",
    "stale-auth-context": "The signed-in account changed during the identity operation.",
    "ui-deadline": "Identity settings did not respond in time.",
    unauthenticated: "Sign in again to continue.",
    unavailable: "Identity settings are temporarily unavailable.",
  };
  const candidateCode = String(error?.code || "unknown").split("/").pop();
  const code = Object.hasOwn(messages, candidateCode) ? candidateCode : "unknown";
  return new IdentityAuthorityServiceError(
    code,
    messages[code] || "The identity operation could not be completed.",
  );
}

function authenticatedUid(authInstance) {
  const uid = authInstance?.currentUser?.uid;
  if (typeof uid !== "string" || uid.length < 1 || uid.length > 128) {
    fail("unauthenticated", "Sign in again to continue.");
  }
  return uid;
}

export function createIdentityAuthorityOperationId(
  cryptoProvider = globalThis.crypto,
) {
  if (typeof cryptoProvider?.randomUUID === "function") {
    return cryptoProvider.randomUUID().replaceAll("-", "_");
  }
  if (typeof cryptoProvider?.getRandomValues === "function") {
    const bytes = cryptoProvider.getRandomValues(new Uint8Array(24));
    return `identity_${Array.from(bytes, (byte) =>
      byte.toString(16).padStart(2, "0"),
    ).join("")}`;
  }
  fail(
    "failed-precondition",
    "Secure identity operations are unavailable.",
  );
}

export function createIdentityAuthorityService({
  authInstance = auth,
  functionsInstance = functions,
  callableFactory = httpsCallable,
  readsEnabled = isIdentityAuthorityReadsFeatureEnabled,
  commandsEnabled = isIdentityAuthorityCommandsFeatureEnabled,
} = {}) {
  const callables = new Map();
  const callableFor = (name) => {
    if (!callables.has(name)) {
      callables.set(name, callableFactory(functionsInstance, name));
    }
    return callables.get(name);
  };
  const invoke = async (name, request, { command = false, authenticated = true } = {}) => {
    if (command ? !commandsEnabled : !readsEnabled) fail("feature-disabled");
    const expectedUid = authenticated ? authenticatedUid(authInstance) : null;
    try {
      const response = await withUiDeadline(callableFor(name)(request), {
        phase: command ? "updating identity" : "loading identity",
        timeoutMs: command ? 30_000 : 15_000,
      });
      if (authenticated && authInstance?.currentUser?.uid !== expectedUid) {
        fail("stale-auth-context");
      }
      return response?.data;
    } catch (error) {
      throw normalizeServiceError(error);
    }
  };

  return Object.freeze({
    async getOwnAccount() {
      const value = await invoke("getOwnAccount", {
        schemaVersion: SCHEMA_VERSION,
      });
      return normalizeOwnAccount(value);
    },

    async getProfilePresentation(publicIdentifierValue) {
      const publicIdentifier = normalizePublicIdentifier(publicIdentifierValue);
      const authenticated = Boolean(authInstance?.currentUser?.uid);
      const value = await invoke(
        "getProfilePresentation",
        { schemaVersion: SCHEMA_VERSION, publicIdentifier },
        { authenticated },
      );
      return normalizeProfilePresentation(value, publicIdentifier);
    },

    async updateOwnAccount(intentValue) {
      requireExactKeys(intentValue, ["operationId", "expectedRevision", "account"]);
      const request = Object.freeze({
        schemaVersion: SCHEMA_VERSION,
        operationId: normalizeOperationId(intentValue.operationId),
        expectedRevision: normalizeRevision(intentValue.expectedRevision, { minimum: 1 }),
        account: normalizeAccountInput(intentValue.account),
      });
      const value = await invoke("updateOwnAccount", request, { command: true });
      return normalizeSimpleRevisionResult(value);
    },

    async setOwnProfilePrivacy(intentValue) {
      requireExactKeys(intentValue, ["operationId", "expectedRevision", "audience", "fields"]);
      if (!PROFILE_AUDIENCES.has(intentValue.audience)) fail("invalid-argument");
      const request = Object.freeze({
        schemaVersion: SCHEMA_VERSION,
        operationId: normalizeOperationId(intentValue.operationId),
        expectedRevision: normalizeRevision(intentValue.expectedRevision),
        audience: intentValue.audience,
        fields: normalizeProfileFields(intentValue.fields),
      });
      const value = await invoke("setOwnProfilePrivacy", request, { command: true });
      return normalizePrivacyResult(value, request);
    },

    async setOwnMembershipDisclosure(intentValue) {
      requireExactKeys(
        intentValue,
        [
          "operationId",
          "expectedRevision",
          "roleAssignmentId",
          "audience",
          "acceptedDisclosureEpoch",
        ],
      );
      if (
        !HOLDER_AUDIENCES.has(intentValue.audience) ||
        (intentValue.acceptedDisclosureEpoch !== null &&
          (!Number.isSafeInteger(intentValue.acceptedDisclosureEpoch) ||
            intentValue.acceptedDisclosureEpoch < 0))
      ) {
        fail("invalid-argument");
      }
      const request = Object.freeze({
        schemaVersion: SCHEMA_VERSION,
        operationId: normalizeOperationId(intentValue.operationId),
        expectedRevision: normalizeRevision(intentValue.expectedRevision, { minimum: 1 }),
        roleAssignmentId: normalizeSha256(intentValue.roleAssignmentId),
        audience: intentValue.audience,
        acceptedDisclosureEpoch: intentValue.acceptedDisclosureEpoch,
      });
      const value = await invoke("setOwnMembershipDisclosure", request, {
        command: true,
      });
      return normalizeDisclosureResult(value, request);
    },

    async setExternalWalletConsent(intentValue) {
      requireExactKeys(
        intentValue,
        ["operationId", "expectedRevision", "walletLinkId", "audience"],
      );
      if (!EXTERNAL_WALLET_AUDIENCES.has(intentValue.audience)) {
        fail("invalid-argument");
      }
      const request = Object.freeze({
        schemaVersion: SCHEMA_VERSION,
        operationId: normalizeOperationId(intentValue.operationId),
        expectedRevision: normalizeRevision(intentValue.expectedRevision, { minimum: 1 }),
        walletLinkId: normalizeSha256(intentValue.walletLinkId),
        audience: intentValue.audience,
      });
      const value = await invoke("setExternalWalletConsent", request, {
        command: true,
      });
      return normalizeWalletConsentResult(value, request);
    },
  });
}

export const identityAuthorityService = createIdentityAuthorityService();
